import { Chip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { FinanceStatus } from '../../styles/tokens/status.js';
import {
  getStatusPresentation,
  normalizeFinanceStatus,
} from '../../styles/tokens/status.js';
import { useAppI18n } from '../../i18n/use-app-i18n';

export interface StatusChipProps {
  status: FinanceStatus | string;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
  /** Localized label supplied by an enum descriptor while tone stays canonical. */
  label?: string;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
}

function fallbackStatusLabel(status: string): string {
  return status.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase();
}

/**
 * StatusChip — canonical status presentation.
 *
 * Known statuses resolve through the central registry. Forward-compatible
 * unknown values retain their actual label while using the neutral fallback
 * tone; they are never silently represented as another workflow state.
 */
export function StatusChip({
  status,
  size = 'small',
  variant = 'outlined',
  label: labelOverride,
  sx,
  'data-testid': dataTestId,
}: StatusChipProps) {
  const { text } = useAppI18n();
  const rawStatus = String(status);
  const normalized = normalizeFinanceStatus(rawStatus);
  const presentation = getStatusPresentation(rawStatus);
  const label = labelOverride ?? (normalized ? text(presentation.labelKey) : fallbackStatusLabel(rawStatus));

  return (
    <Chip
      label={label}
      color={presentation.color}
      size={size}
      variant={variant}
      data-status={rawStatus}
      data-status-known={normalized ? 'true' : 'false'}
      data-status-tone={presentation.tone}
      data-testid={dataTestId}
      sx={sx}
    />
  );
}
